import type { Role } from '@buildbee/contracts';

/**
 * Settings navigation, grouped the way an owner thinks about it: the company, the people, money,
 * and data. Each link shows only to people who can use it (the API still checks every action).
 * Your own profile lives in the account menu, not here.
 */

export type SettingsNavLink = { to: string; label: string; exact?: boolean; needs?: string };
export type SettingsNavGroup = { label: string; links: SettingsNavLink[] };

export const SETTINGS_NAV: readonly SettingsNavGroup[] = [
  {
    label: 'Company',
    links: [
      { to: '/settings', label: 'Company details', exact: true },
      { to: '/settings/branding', label: 'Branding', needs: 'settings:manage' },
      { to: '/settings/automations', label: 'Automations', needs: 'settings:manage' },
    ],
  },
  {
    label: 'Members & roles',
    links: [
      { to: '/settings/members', label: 'Members', needs: 'member:view' },
      { to: '/settings/roles', label: 'Roles & permissions', needs: 'member:view' },
    ],
  },
  {
    label: 'Billing',
    links: [{ to: '/settings/billing', label: 'Plan & billing', needs: 'billing:manage' }],
  },
  {
    label: 'Data',
    links: [
      { to: '/settings/custom-fields', label: 'Custom fields', needs: 'custom_field:manage' },
      // Export is hidden for now; the page still lives at /settings/export.
      { to: '/settings/audit', label: 'Activity log', needs: 'audit:view' },
    ],
  },
];

/**
 * The groups and links a viewer sees. `cells` is the viewer's workspace-level permissions
 * (`workspace.roles.mine`); until it loads only links that need nothing are shown.
 */
export function settingsNavFor(role: Role | null, cells: readonly string[] | null): SettingsNavGroup[] {
  const has = (cell: string) => role === 'owner' || !!cells?.includes(cell);
  return SETTINGS_NAV.map((g) => ({ ...g, links: g.links.filter((l) => !l.needs || has(l.needs)) })).filter((g) => g.links.length);
}
